import { useState, useMemo, useCallback } from 'react';
import { ColDef, CellValueChangedEvent, ICellRendererParams } from 'ag-grid-community';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ChevronDown, X, Sparkles, Save, Loader2, Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AGDataGrid } from '@/components/ui/ag-data-grid';

export interface QuoteLineItem {
  sequence: number;
  vendor_itemcode?: string;
  vendor_short_description?: string;
  vendor_long_description?: string;
  vendor_quantity?: number;
  vendor_unit_price?: number;
  vendor_price?: number;
  vendor_uom?: string;
  confidence?: number;
  notes?: string;
}

export interface QuoteExtractionData {
  vendor_name?: string;
  contact_person?: string;
  contact_phone?: string;
  contact_email?: string;
  vendor_address?: string;
  well_name?: string;
  well_api?: string;
  measured_depth?: string;
  true_vertical_depth?: string;
  h2s_present?: string;
  conveyance_type?: string;
  vendor_quote_number?: string;
  effective_date?: string;
  expiration_date?: string;
  vendor_notes?: string;
  confidence?: number;
  explanation?: string;
  line_items: QuoteLineItem[];
}

export interface QuoteExtractionResult {
  success: boolean;
  confidence: number;
  data: QuoteExtractionData;
  debug?: { processing_time_ms: number };
}

interface Vendor {
  id: string;
  name: string;
}

interface HeaderData {
  well_name: string;
  well_api: string;
  vendor_quote_number: string;
  effective_date: string;
  expiration_date: string;
  vendor_notes: string;
}

interface Props {
  results: QuoteExtractionResult;
  headerData: HeaderData;
  lineItems: QuoteLineItem[];
  onHeaderChange: (field: string, value: string) => void;
  onLineItemsChange: (items: QuoteLineItem[]) => void;
  onClear: () => void;
  onSave: () => void;
  isSaving: boolean;
  vendors: Vendor[];
  selectedVendorId: string | null;
  onVendorChange: (vendorId: string | null) => void;
  isAddingVersion?: boolean;
  hideToolbar?: boolean;
  hideHeader?: boolean;
  emailText?: string;
}

interface QuoteExtractionHeaderProps {
  results: QuoteExtractionResult;
  headerData: HeaderData;
  onHeaderChange: (field: string, value: string) => void;
  vendors: Vendor[];
  selectedVendorId: string | null;
  onVendorChange: (vendorId: string | null) => void;
  isAddingVersion?: boolean;
  emailText?: string;
}

export function QuoteExtractionHeader({
  results,
  headerData,
  onHeaderChange,
  vendors,
  selectedVendorId,
  onVendorChange,
  isAddingVersion = false,
  emailText,
}: QuoteExtractionHeaderProps) {
  const [vendorOpen, setVendorOpen] = useState(false);
  const [headerExpanded, setHeaderExpanded] = useState(true);

  const selectedVendor = vendors.find((v) => v.id === selectedVendorId);

  const headerCard = (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Extracted Quote Data
            </CardTitle>
            {!headerExpanded && (
              <div className="text-sm text-muted-foreground mt-1">
                {selectedVendor?.name || 'No vendor selected'} • Quote #{headerData.vendor_quote_number || 'N/A'}
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setHeaderExpanded(!headerExpanded)}
          >
            <ChevronDown
              className={`w-4 h-4 transition-transform ${headerExpanded ? 'rotate-180' : ''}`}
            />
          </Button>
        </div>
      </CardHeader>
      <Collapsible open={headerExpanded} onOpenChange={setHeaderExpanded}>
        <CollapsibleContent>
          <CardContent className="space-y-4">
            {/* Vendor picklist */}
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                Vendor / Service Provider
              </p>
              <Popover open={vendorOpen} onOpenChange={setVendorOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={vendorOpen}
                    disabled={isAddingVersion}
                    className={cn(
                      "w-full max-w-md justify-between h-auto min-h-[36px] py-1",
                      isAddingVersion && "bg-muted cursor-not-allowed"
                    )}
                  >
                    <span className="truncate text-left">
                      {selectedVendor
                        ? selectedVendor.name
                        : results.data.vendor_name || 'Select vendor...'}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search vendors..." />
                    <CommandList>
                      <CommandEmpty>No vendor found.</CommandEmpty>
                      <CommandGroup>
                        {vendors.map((vendor) => (
                          <CommandItem
                            key={vendor.id}
                            value={vendor.name}
                            onSelect={() => {
                              onVendorChange(vendor.id === selectedVendorId ? null : vendor.id);
                              setVendorOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                selectedVendorId === vendor.id ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            {vendor.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedVendor && selectedVendor.name !== results.data.vendor_name && (
                <p className="text-xs text-muted-foreground mt-1">
                  Extracted: {results.data.vendor_name}
                </p>
              )}
              {!selectedVendorId && (
                <p className="text-xs text-destructive mt-1">
                  A vendor must be selected to save.
                </p>
              )}
            </div>

            {/* Editable header fields */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {!isAddingVersion && (
                <>
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider">Well Name</label>
                    <Input
                      value={headerData.well_name}
                      onChange={(e) => onHeaderChange('well_name', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider">Well API</label>
                    <Input
                      value={headerData.well_api}
                      onChange={(e) => onHeaderChange('well_api', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </>
              )}
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider">Quote #</label>
                <Input
                  value={headerData.vendor_quote_number}
                  onChange={(e) => onHeaderChange('vendor_quote_number', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider">Effective Date</label>
                <Input
                  type="date"
                  value={headerData.effective_date}
                  onChange={(e) => onHeaderChange('effective_date', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider">Expiration Date</label>
                <Input
                  type="date"
                  value={headerData.expiration_date}
                  onChange={(e) => onHeaderChange('expiration_date', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Additional extraction details (read-only) */}
            {(results.data.contact_person || results.data.contact_phone || results.data.contact_email ||
              results.data.vendor_address || results.data.measured_depth || results.data.true_vertical_depth ||
              results.data.h2s_present || results.data.conveyance_type) && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-3 border-t border-border">
                {results.data.contact_person && (
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider">Contact Person</label>
                    <p className="mt-1 text-sm">{results.data.contact_person}</p>
                  </div>
                )}
                {results.data.contact_phone && (
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider">Phone</label>
                    <p className="mt-1 text-sm">{results.data.contact_phone}</p>
                  </div>
                )}
                {results.data.contact_email && (
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider">Email</label>
                    <p className="mt-1 text-sm">{results.data.contact_email}</p>
                  </div>
                )}
                {results.data.vendor_address && (
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider">Address</label>
                    <p className="mt-1 text-sm">{results.data.vendor_address}</p>
                  </div>
                )}
                {results.data.measured_depth && (
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider">Measured Depth</label>
                    <p className="mt-1 text-sm">{results.data.measured_depth}</p>
                  </div>
                )}
                {results.data.true_vertical_depth && (
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider">True Vertical Depth</label>
                    <p className="mt-1 text-sm">{results.data.true_vertical_depth}</p>
                  </div>
                )}
                {results.data.h2s_present && (
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider">H2S Present</label>
                    <p className="mt-1 text-sm">{results.data.h2s_present}</p>
                  </div>
                )}
                {results.data.conveyance_type && (
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider">Conveyance Type</label>
                    <p className="mt-1 text-sm">{results.data.conveyance_type}</p>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider">Notes</label>
              <Textarea
                value={headerData.vendor_notes}
                onChange={(e) => onHeaderChange('vendor_notes', e.target.value)}
                className="mt-1"
                rows={3}
              />
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );

  if (!emailText?.trim()) return headerCard;

  return (
    <div className="grid grid-cols-3 gap-4">
      <div className="col-span-2">{headerCard}</div>
      <div className="col-span-1 relative">
        <Card className="absolute inset-0 flex flex-col">
          <CardHeader className="pb-3 shrink-0">
            <CardTitle className="text-lg">Source Email Conversation</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden">
            <div className="whitespace-pre-wrap text-sm text-muted-foreground overflow-y-auto h-full">
              {emailText}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function QuoteExtractionResults({
  results,
  headerData,
  lineItems,
  onHeaderChange,
  onLineItemsChange,
  onClear,
  onSave,
  isSaving,
  vendors,
  selectedVendorId,
  onVendorChange,
  isAddingVersion = false,
  hideToolbar = false,
  hideHeader = false,
  emailText,
}: Props) {
  const [showRawJson, setShowRawJson] = useState(false);
  const [vendorOpen, setVendorOpen] = useState(false);
  const [headerExpanded, setHeaderExpanded] = useState(true);
  const [explanationExpanded, setExplanationExpanded] = useState(false);

  const selectedVendor = vendors.find((v) => v.id === selectedVendorId);
  const selectedVendorName = selectedVendor?.name || 'No vendor selected';

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 8) return 'bg-green-500';
    if (confidence >= 4) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // AG Grid: handle cell edits
  const handleCellValueChanged = useCallback(
    (event: CellValueChangedEvent<QuoteLineItem>) => {
      const updated = [...lineItems];
      const idx = updated.findIndex((item) => item.sequence === event.data?.sequence);
      if (idx >= 0 && event.data) {
        const item = { ...event.data };
        // Recalculate vendor_price when qty or unit price changes
        if (event.column.getColId() === 'vendor_quantity' || event.column.getColId() === 'vendor_unit_price') {
          item.vendor_price = (item.vendor_quantity ?? 0) * (item.vendor_unit_price ?? 0);
        }
        updated[idx] = item;
        onLineItemsChange(updated);
      }
    },
    [lineItems, onLineItemsChange]
  );

  const columnDefs = useMemo<ColDef<QuoteLineItem>[]>(
    () => [
      {
        field: 'confidence',
        headerName: 'Conf.',
        width: 85,
        editable: false,
        cellRenderer: (params: ICellRendererParams<QuoteLineItem>) => {
          const conf = params.data?.confidence;
          if (conf == null) return null;
          const color = conf >= 8 ? 'bg-green-500' : conf >= 4 ? 'bg-yellow-500' : 'bg-red-500';
          return (
            <div className="flex items-center gap-1.5 h-full" title={params.data?.notes || ''}>
              <span className={`inline-block w-2.5 h-2.5 rounded-full ${color}`} />
              <span className="text-xs text-muted-foreground">{conf}/10</span>
            </div>
          );
        },
      },
      {
        field: 'vendor_itemcode',
        headerName: 'Item Code',
        width: 130,
        editable: true,
        valueFormatter: (p) => p.value || '',
      },
      {
        field: 'sequence',
        headerName: 'Line#',
        width: 70,
        editable: false,
        type: 'rightAligned',
        headerClass: 'ag-right-aligned-header',
      },
      {
        field: 'vendor_short_description',
        headerName: 'Short Description',
        flex: 1,
        minWidth: 180,
        editable: true,
        valueFormatter: (p) => p.value || '',
      },
      {
        field: 'vendor_long_description',
        headerName: 'Long Description',
        flex: 1,
        minWidth: 180,
        editable: true,
        valueFormatter: (p) => p.value || '',
      },
      {
        field: 'vendor_quantity',
        headerName: 'Qty',
        width: 90,
        editable: true,
        type: 'rightAligned',
        headerClass: 'ag-right-aligned-header',
        valueFormatter: (p) => (p.value != null ? String(p.value) : ''),
      },
      {
        field: 'vendor_unit_price',
        headerName: 'Unit Price',
        width: 120,
        editable: true,
        type: 'rightAligned',
        headerClass: 'ag-right-aligned-header',
        valueFormatter: (p) => (p.value != null ? Number(p.value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''),
      },
      {
        field: 'vendor_price',
        headerName: 'Price',
        width: 120,
        editable: false,
        type: 'rightAligned',
        headerClass: 'ag-right-aligned-header',
        valueGetter: (p) => {
          const qty = p.data?.vendor_quantity ?? 0;
          const unit = p.data?.vendor_unit_price ?? 0;
          return qty * unit;
        },
        valueFormatter: (p) => (p.value != null ? Number(p.value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''),
      },
      {
        field: 'vendor_uom',
        headerName: 'UOM',
        width: 100,
        editable: true,
        valueFormatter: (p) => p.value || '',
      },
      
    ],
    []
  );

  return (
    <div className="space-y-6">
      {/* Header with extraction info */}
      {!hideToolbar && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Confidence:</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getConfidenceColor(results.confidence)} transition-all`}
                      style={{ width: `${(results.confidence / 10) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium">{results.confidence}/10</span>
                </div>
              </div>
              {results.debug?.processing_time_ms && (
                <span className="text-xs text-muted-foreground">
                  {(results.debug.processing_time_ms / 1000).toFixed(1)}s
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={onClear}>
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
              <Button size="sm" onClick={onSave} disabled={isSaving || !selectedVendorId}>
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-1" />
                    Save Quote
                  </>
                )}
              </Button>
            </div>
          </div>
          {results.data.explanation && (
            <div className="text-sm text-muted-foreground">
              {explanationExpanded || results.data.explanation.length <= 200 ? (
                <span>
                  {results.data.explanation}
                  {results.data.explanation.length > 200 && (
                    <button
                      onClick={() => setExplanationExpanded(false)}
                      className="ml-1 text-primary hover:underline"
                    >
                      Show less
                    </button>
                  )}
                </span>
              ) : (
                <span>
                  {results.data.explanation.slice(0, 200)}...
                  <button
                    onClick={() => setExplanationExpanded(true)}
                    className="ml-1 text-primary hover:underline"
                  >
                    Show more
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Header Data */}
      {!hideHeader && (
        <QuoteExtractionHeader
          results={results}
          headerData={headerData}
          onHeaderChange={onHeaderChange}
          vendors={vendors}
          selectedVendorId={selectedVendorId}
          onVendorChange={onVendorChange}
          isAddingVersion={isAddingVersion}
          emailText={emailText}
        />
      )}

      {/* Line Items - Editable AG Grid */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            Extracted Quote Line Items ({lineItems.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <AGDataGrid<QuoteLineItem>
            rowData={lineItems}
            columnDefs={columnDefs}
            columnStateKey="quote_extraction_items"
            totalCount={lineItems.length}
            pageIndex={0}
            pageSize={50}
            pageSizeOptions={[10, 50, 100]}
            isEditMode={true}
            showEditToggle={true}
            onEditModeChange={() => {}}
            onCellValueChanged={handleCellValueChanged}
            height="550px"
          />
        </CardContent>
      </Card>

      {/* Raw JSON */}
      <Collapsible open={showRawJson} onOpenChange={setShowRawJson}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="w-full justify-between">
            <span className="text-muted-foreground">Raw JSON Response</span>
            <ChevronDown
              className={`w-4 h-4 transition-transform ${showRawJson ? 'rotate-180' : ''}`}
            />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Card className="mt-2">
            <CardContent className="p-4">
              <pre className="text-xs overflow-auto max-h-96 bg-muted p-4 rounded-lg">
                {JSON.stringify(results, null, 2)}
              </pre>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
